// Reine Sprach-Auflösung — getrennt vom React-Teil, damit sie unit-testbar
// bleibt (node:test, ohne DOM).

/**
 * Ermittelt die Startsprache aus (in dieser Reihenfolge):
 *  1. gespeicherter Wahl (localStorage-Wert, sofern verfügbar),
 *  2. den Browser-Sprachen (erst exakter Treffer wie "es-ar", dann Basis "es"),
 *  3. dem Fallback.
 *
 * `available` sind die registrierten Wörterbuch-Sprachen (z. B. ["de","es","en"]).
 */
export function resolveInitialLang(
  available: readonly string[],
  stored: string | null | undefined,
  navigatorLangs: readonly string[],
  fallback: string,
): string {
  if (stored && available.includes(stored)) return stored;
  for (const raw of navigatorLangs) {
    const lang = raw.toLowerCase();
    if (available.includes(lang)) return lang;
    const base = lang.split("-")[0];
    if (base && available.includes(base)) return base;
  }
  return fallback;
}

/**
 * Endonyme für Sprachen, für die `Intl.DisplayNames` keine Daten mitbringt.
 *
 * ⚠️ Ohne diese Tabelle steht im Sprachumschalter der ENGLISCHE Name — für
 * Georgisch liefert Chrome „Georgian" statt „ქართული" (geprüft:
 * `Intl.DisplayNames.supportedLocalesOf(["ka"])` ist leer). Ausgerechnet im
 * Umschalter ist das fatal: wer nur Georgisch liest, sucht dort nach dem
 * einzigen Eintrag, den er erkennt.
 *
 * Nur für Sprachen pflegen, die wir wirklich anbieten — für alles andere ist
 * `Intl` die bessere Quelle (aktueller und vollständiger als jede Handliste).
 */
const ENDONYMS: Record<string, string> = {
  ka: "ქართული",
};

/**
 * Endonym einer Sprache (Name in der Sprache selbst): "de" → "Deutsch",
 * "es" → "español". Für den Sprachumschalter — jede Sprache ist für ihre
 * Sprecher lesbar, egal welche gerade aktiv ist. Fallback: der Code selbst.
 */
export function languageName(code: string): string {
  const own = ENDONYMS[code];
  if (own) return own;
  try {
    const name = new Intl.DisplayNames([code], { type: "language" }).of(code);
    if (!name || name === code) return code;
    return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
  } catch {
    return code;
  }
}
